import type { MapTaskView } from "../types";
import { docAnchorId } from "./SearchResultsView";
import { LABEL } from "./styles";

/** Per-paper answers from a map run, as a paper → model-answer table.
 * Clicking a paper opens its blocks. */
export const MapResultsView = ({
  question,
  resultsId,
  tasks,
  onOpenPaper,
}: {
  question: string;
  resultsId: string;
  tasks: MapTaskView[];
  onOpenPaper: (docId: string) => void;
}) => {
  if (tasks.length === 0) {
    return (
      <p className="py-12 text-center text-base italic text-zinc-500">
        The map run returned no per-paper answers. Check the Terminal tab for
        the raw output.
      </p>
    );
  }

  const succeeded = tasks.filter((t) => t.status === "success").length;

  return (
    <div>
      <div className="mb-3 flex items-baseline gap-3 border-b border-zinc-200 pb-2 text-sm text-zinc-400">
        <span className="text-zinc-700">
          {succeeded}/{tasks.length} papers answered
        </span>
        <span className="font-mono text-2xs">{resultsId}</span>
        <span className="ml-auto hidden truncate italic sm:block" title={question}>
          “{question}”
        </span>
      </div>

      <div className="overflow-hidden">
        <table className="w-full table-fixed border-collapse text-left">
          <thead>
            <tr className="border-b border-zinc-300">
              <th
                scope="col"
                className={`${LABEL} w-[38%] pb-2 pr-4`}
              >
                Paper
              </th>
              <th
                scope="col"
                className={`${LABEL} pb-2`}
              >
                Model answer
              </th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task, i) => {
              const ok = task.status === "success";
              return (
                <tr
                  key={`${task.documentId || i}`}
                  id={task.documentId ? docAnchorId(task.documentId) : undefined}
                  className="scroll-mt-24 border-b border-zinc-200/70 align-top last:border-b-0"
                >
                  <td className="py-3 pr-4">
                    <div className="flex items-start gap-2">
                      <span
                        aria-hidden
                        className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${ok ? "bg-zinc-400" : "bg-red-500"}`}
                      />
                      <div className="min-w-0">
                        {task.documentId ? (
                          <button
                            type="button"
                            onClick={() => onOpenPaper(task.documentId)}
                            className="block text-left text-base leading-6 text-zinc-900 transition-colors hover:text-zinc-600"
                          >
                            {task.title || task.documentId}
                          </button>
                        ) : (
                          <span className="block text-base leading-6 text-zinc-900">
                            {task.title || "Untitled"}
                          </span>
                        )}
                        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 font-mono text-2xs text-zinc-400">
                          {task.documentId && <span>{task.documentId}</span>}
                          {task.timeMs != null && (
                            <span className="tabular-nums">
                              {(task.timeMs / 1000).toFixed(1)}s
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3">
                    {ok ? (
                      <p className="whitespace-pre-wrap text-sm leading-6 text-zinc-700">
                        {task.answer || "—"}
                      </p>
                    ) : (
                      <p className="text-sm italic text-red-700">
                        {task.error || "Reader failed on this paper."}
                      </p>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
